import React from 'react';
import { ResponsiveContainer, Treemap, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceArea, ReferenceLine } from 'recharts';
import PrintSection from './PrintSection';
import AverageTalkTimeChart from '../charts/AverageTalkTimeChart';
import CommentsVsQuestionsChart from '../charts/CommentsVsQuestionsChart';
import FrequencyChart from '../charts/FrequencyChart';
import { isInquiry, compressTimeGaps } from '../../utils/metrics';

const COLORS = ['#2a3d5b', '#5f7942', '#cba43a', '#922b3f', '#7209b7', '#4cc9f0'];

// content: matching doc from reportContent Firestore collection
// reportData: full object from buildReportData()
// meeting: the meeting Firestore document

export function PrintSharingSpace({ pageNumber, content, reportData, chartOnly }) {
  // participation.speakers: [{ name, ms, pct, turns }]
  const treemapData = (reportData.participation?.speakers || [])
    .slice()
    .sort((a, b) => (b.pct || 0) - (a.pct || 0))
    .map((p, i) => ({
      name: p.name.split('@')[0],
      size: p.pct || 1,
      color: COLORS[i % COLORS.length],
    }));

  return (
    <PrintSection
      pageNumber={pageNumber}
      eyebrow={content?.eyebrow || 'Healthy Behaviors'}
      title={content?.title || 'Sharing Space'}
      body={content?.body || ''}
      pullQuote={content?.pullQuote}
      pullQuoteSource={content?.pullQuoteSource}
      chartTitle={content?.chartTitle || 'Occupying Meeting Space'}
      chartOnly={chartOnly}
    >
      <ResponsiveContainer width="100%" height={chartOnly ? 600 : 400}>
        <Treemap
          data={treemapData}
          dataKey="size"
          aspectRatio={4 / 3}
          isAnimationActive={false}
          fill="transparent"
          content={({ x, y, width, height, name, color }) => {
            const showLabel = width > 40 && height > 28;
            return (
              <g>
                <rect x={x + 1} y={y + 1} width={Math.max(width - 2, 0)} height={Math.max(height - 2, 0)} fill={color} rx={6} ry={6} />
                {showLabel && (
                  <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="0.75rem" fontWeight={700}>
                    {name}
                  </text>
                )}
              </g>
            );
          }}
        />
      </ResponsiveContainer>
    </PrintSection>
  );
}

const ADVOCACY_PER_PAGE = 12;

export function PrintAdvocacy({ pageNumber, content, reportData, chartOnly }) {
  const allPeople = [...(reportData.inquiry?.perPerson || [])].sort((a, b) => {
    const aTotal = a.inquiry + a.advocacy;
    const bTotal = b.inquiry + b.advocacy;
    const aPct = aTotal ? a.inquiry / aTotal : 0;
    const bPct = bTotal ? b.inquiry / bTotal : 0;
    return bPct - aPct;
  });
  const pages = [];
  for (let i = 0; i < Math.max(allPeople.length, 1); i += ADVOCACY_PER_PAGE) {
    pages.push(allPeople.slice(i, i + ADVOCACY_PER_PAGE));
  }

  return pages.map((chunk, pi) => {
    if (chartOnly && pi > 0) return null;
    return (
      <PrintSection
        key={pi}
        pageNumber={pi === 0 ? pageNumber : null}
        eyebrow={content?.eyebrow || 'Healthy Behaviors'}
        title={pi === 0 ? (content?.title || 'Advocacy vs. Inquiry') : 'Advocacy vs. Inquiry (cont.)'}
        body={pi === 0 ? (content?.body || '') : ''}
        pullQuote={pi === 0 ? content?.pullQuote : null}
        pullQuoteSource={pi === 0 ? content?.pullQuoteSource : null}
        chartTitle={content?.chartTitle || 'Comments vs. Questions'}
        chartOnly={chartOnly}
      >
        <div style={{ width: '100%' }}>
          <CommentsVsQuestionsChart inquiry={{ perPerson: chunk }} printMode={true} />
        </div>
      </PrintSection>
    );
  });
}

const BREVITY_PER_PAGE = 25;

export function PrintBrevity({ pageNumber, content, reportData, chartOnly }) {
  const allSpeakers = (reportData.participation?.speakers || [])
    .slice()
    .sort((a, b) => {
      const avgA = a.turns > 0 ? a.ms / a.turns : 0;
      const avgB = b.turns > 0 ? b.ms / b.turns : 0;
      return avgB - avgA;
    });
  const pages = [];
  for (let i = 0; i < Math.max(allSpeakers.length, 1); i += BREVITY_PER_PAGE) {
    pages.push(allSpeakers.slice(i, i + BREVITY_PER_PAGE));
  }

  return pages.map((chunk, pi) => {
    if (chartOnly && pi > 0) return null;
    return (
      <PrintSection
        key={pi}
        pageNumber={pi === 0 ? pageNumber : null}
        eyebrow={content?.eyebrow || 'Healthy Behaviors'}
        title={pi === 0 ? (content?.title || 'Brevity') : 'Brevity (cont.)'}
        body={pi === 0 ? (content?.body || '') : ''}
        pullQuote={pi === 0 ? content?.pullQuote : null}
        pullQuoteSource={pi === 0 ? content?.pullQuoteSource : null}
        chartTitle={content?.chartTitle || 'Average Talk Time'}
        chartOnly={chartOnly}
      >
        <div style={{ width: '100%' }}>
          <AverageTalkTimeChart
            participation={{ speakers: chunk }}
            colors={COLORS}
            printMode={true}
          />
        </div>
      </PrintSection>
    );
  });
}

export function PrintFrequency({ pageNumber, content, reportData, chartOnly }) {
  return (
    <PrintSection
      pageNumber={pageNumber}
      eyebrow={content?.eyebrow || 'Healthy Behaviors'}
      title={content?.title || 'Centered on Issues vs. People'}
      body={content?.body || ''}
      pullQuote={content?.pullQuote}
      pullQuoteSource={content?.pullQuoteSource}
      chartTitle={content?.chartTitle || 'Speaking Frequency'}
      chartOnly={chartOnly}
    >
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <FrequencyChart frequency={reportData.frequency || []} printMode={true} />
      </div>
    </PrintSection>
  );
}

function BandLabel({ viewBox, value }) {
  if (!value || !viewBox) return null;
  const { x, y, width } = viewBox;
  if (width < 20) return null;
  const label = value.length > 22 ? value.slice(0, 21) + '…' : value;
  const cx = x + width / 2;
  const cy = y + 4;
  return (
    <text x={cx} y={cy} transform={`rotate(90, ${cx}, ${cy})`} textAnchor="start" dominantBaseline="middle" fontSize={8} fill="#666">
      {label}
    </text>
  );
}

function KeyMomentStar({ viewBox }) {
  if (!viewBox) return null;
  const { x, y } = viewBox;
  return (
    <text x={x} y={(y || 0) + 14} textAnchor="middle" fontSize={12} fill="#4361ee">★</text>
  );
}

function KeyMomentStarH({ viewBox }) {
  // For vertical layout (chartOnly): star to the right of the line
  if (!viewBox) return null;
  const { x, y, height } = viewBox;
  return (
    <text x={(x || 0) + 6} y={y + height / 2 + 4} textAnchor="start" fontSize={10} fill="#4361ee">★</text>
  );
}

function HorizontalBandLabel({ viewBox, value }) {
  if (!value || !viewBox) return null;
  const { x, y, width, height } = viewBox;
  if (height < 10) return null;
  const label = value.length > 18 ? value.slice(0, 17) + '…' : value;
  return (
    <text x={x + width - 4} y={y + height / 2} textAnchor="end" dominantBaseline="middle" fontSize={8} fill="#555">
      {label}
    </text>
  );
}

export function PrintDialogueFrequency({ pageNumber, content, entries = [], chartOnly, chartTitleOverride }) {
  const WINDOW_MS = 2 * 60 * 1000;
  const sorted = compressTimeGaps(entries)
    .filter(e => e.type !== 'break' && e.startTime)
    .sort((a, b) => a.startTime - b.startTime);

  let lineData = [];
  if (sorted.length > 0) {
    const start = sorted[0].startTime;
    const end = sorted[sorted.length - 1].endTime || sorted[sorted.length - 1].startTime;
    const numBuckets = Math.max(Math.ceil((end - start) / WINDOW_MS), 1);

    for (let b = 0; b < numBuckets; b++) {
      const bucketStart = start + b * WINDOW_MS;
      const bucketEnd = bucketStart + WINDOW_MS;
      const bucketMid = bucketStart + WINDOW_MS / 2;
      const bucket = { time: `${Math.round((b * WINDOW_MS) / 60000)}m` };

      let activeSection = null;
      let activeTopic = null;
      for (let i = sorted.length - 1; i >= 0; i--) {
        if (sorted[i].startTime <= bucketMid) {
          if (!activeSection && sorted[i].section) activeSection = sorted[i].section;
          if (!activeTopic && sorted[i].topic) activeTopic = sorted[i].topic;
          if (activeSection || activeTopic) break;
        }
      }
      if (activeTopic) bucket._topic = activeTopic;
      else if (activeSection) bucket._section = activeSection;

      const speakersInWindow = new Set();
      let questionsInWindow = 0;
      sorted.forEach(e => {
        if (e.startTime >= bucketStart && e.startTime < bucketEnd) {
          speakersInWindow.add(e.speakerDisplayName || e.speakerName?.split('@')[0] || 'Unknown');
          if (isInquiry(e)) questionsInWindow++;
        }
      });
      bucket.uniqueSpeakers = speakersInWindow.size;
      bucket.questionsInWindow = questionsInWindow;
      lineData.push(bucket);
    }
  }

  // Key moment times — entries tagged with /km, mapped to their bucket label
  const kmTimes = [];
  if (sorted.length > 0) {
    const start = sorted[0].startTime;
    sorted.forEach(e => {
      if (e.text?.includes('/km') && e.startTime) {
        const bucketIdx = Math.min(
          Math.floor((e.startTime - start) / WINDOW_MS),
          lineData.length - 1
        );
        const label = lineData[bucketIdx]?.time;
        if (label && !kmTimes.includes(label)) kmTimes.push(label);
      }
    });
  }

  const BAND_COLORS = ['#2a3d5b12', '#5f794212', '#cba43a12', '#922b3f12', '#7209b712', '#4cc9f012'];
  const topicBands = [];
  if (lineData.length > 0) {
    let currentLabel = null;
    let bandStartIdx = 0;
    lineData.forEach((d, i) => {
      const label = d._topic || d._section || null;
      if (label !== currentLabel) {
        if (currentLabel !== null) {
          topicBands.push({ label: currentLabel, x1: lineData[bandStartIdx].time, x2: lineData[i - 1].time, colorIdx: topicBands.length });
        }
        currentLabel = label;
        bandStartIdx = i;
      }
      if (i === lineData.length - 1 && currentLabel !== null) {
        topicBands.push({ label: currentLabel, x1: lineData[bandStartIdx].time, x2: d.time, colorIdx: topicBands.length });
      }
    });
  }

  return (
    <PrintSection
      pageNumber={pageNumber}
      eyebrow={content?.eyebrow || 'Healthy Behaviors'}
      title={content?.title || 'Dialogue Frequency Over Time'}
      body={content?.body || ''}
      pullQuote={content?.pullQuote}
      pullQuoteSource={content?.pullQuoteSource}
      chartTitle={chartTitleOverride || content?.chartTitle || 'Unique Speakers & Questions per 2-min Window'}
      chartOnly={chartOnly}
    >
      <div style={{ width: '100%' }}>
        {lineData.length > 1 ? (
          chartOnly ? (
            <ResponsiveContainer width="100%" height={620}>
              <LineChart data={lineData} layout="vertical" margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0ddd8" />
                <YAxis dataKey="time" type="category" tick={{ fontSize: 8 }} width={28} />
                <XAxis type="number" allowDecimals={false} domain={[0, 'auto']} tick={{ fontSize: 9 }} />
                <Tooltip
                  formatter={(val, name) => [val, name === 'uniqueSpeakers' ? 'Unique speakers' : 'Questions asked']}
                  labelFormatter={(label) => `At ${label}`}
                />
                {topicBands.map((band, i) => (
                  <ReferenceArea
                    key={i}
                    y1={band.x1}
                    y2={band.x2}
                    fill={BAND_COLORS[band.colorIdx % BAND_COLORS.length]}
                    label={<HorizontalBandLabel value={band.label} />}
                  />
                ))}
                {kmTimes.map((t, i) => (
                  <ReferenceLine key={`km-${i}`} y={t} stroke="#4361ee" strokeWidth={1.5} strokeDasharray="4 3" label={{ value: '★', position: 'right', fill: '#4361ee', fontSize: 10 }} />
                ))}
                <Line type="monotone" dataKey="uniqueSpeakers" stroke="#2a3d5b" strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="questionsInWindow" stroke="#cba43a" strokeWidth={2} dot={false} isAnimationActive={false} />
                <Legend content={(props) => {
                  const items = (props.payload || []).map(p => ({ color: p.color, label: p.dataKey === 'uniqueSpeakers' ? 'Unique speakers' : 'Questions asked' }));
                  return (
                    <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', fontSize: 10, paddingTop: 4 }}>
                      {items.map((item, i) => (
                        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ display: 'inline-block', width: 12, height: 3, background: item.color, borderRadius: 2 }} />
                          {item.label}
                        </span>
                      ))}
                      {kmTimes.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: '#4361ee', fontSize: 12, lineHeight: 1 }}>★</span>
                          Key Moments
                        </span>
                      )}
                    </div>
                  );
                }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={lineData} margin={{ left: -10 }}>
                <defs>
                  <linearGradient id="pdfSpeakersGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2a3d5b" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#2a3d5b" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="pdfQuestionsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#cba43a" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#cba43a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0ddd8" vertical={false} />
                <XAxis dataKey="time" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} allowDecimals={false} domain={[0, 'auto']} />
                <Tooltip
                  formatter={(val, name) => [val, name === 'uniqueSpeakers' ? 'Unique speakers' : 'Questions asked']}
                  labelFormatter={(label) => `At ${label}`}
                />
                {topicBands.map((band, i) => (
                  <ReferenceArea
                    key={i}
                    x1={band.x1}
                    x2={band.x2}
                    fill={BAND_COLORS[band.colorIdx % BAND_COLORS.length]}
                    label={<BandLabel value={band.label} />}
                  />
                ))}
                {kmTimes.map((t, i) => (
                  <ReferenceLine key={`km-${i}`} x={t} stroke="#4361ee" strokeWidth={1.5} strokeDasharray="4 3" label={{ value: '★', position: 'insideTop', fill: '#4361ee', fontSize: 10 }} />
                ))}
                <Area type="monotone" dataKey="uniqueSpeakers" stroke="#2a3d5b" strokeWidth={2.5} fill="url(#pdfSpeakersGrad)" dot={false} activeDot={{ r: 3, fill: '#2a3d5b' }} isAnimationActive={false} />
                <Area type="monotone" dataKey="questionsInWindow" stroke="#cba43a" strokeWidth={1.5} fill="url(#pdfQuestionsGrad)" dot={false} activeDot={{ r: 3, fill: '#cba43a' }} isAnimationActive={false} />
                <Legend content={(props) => {
                  const items = (props.payload || []).map(p => ({ color: p.color, label: p.dataKey === 'uniqueSpeakers' ? 'Unique speakers' : 'Questions asked' }));
                  return (
                    <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', fontSize: 10, paddingTop: 4 }}>
                      {items.map((item, i) => (
                        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ display: 'inline-block', width: 12, height: 3, background: item.color, borderRadius: 2 }} />
                          {item.label}
                        </span>
                      ))}
                      {kmTimes.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: '#4361ee', fontSize: 12, lineHeight: 1 }}>★</span>
                          Key Moments
                        </span>
                      )}
                    </div>
                  );
                }} />
              </AreaChart>
            </ResponsiveContainer>
          )
        ) : (
          <div style={{ fontSize: 10, color: '#6b757e', fontStyle: 'italic' }}>Not enough data to display chart.</div>
        )}
      </div>
    </PrintSection>
  );
}
